import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/lib/i18n";
import { getAllArticles } from "@/lib/blogContent";
import { ArticleCard } from "@/components/blog/ArticleCard";
import { BookOpen } from "lucide-react";

const BASE_URL = 'https://pdrn-skin.com';

const TOPIC_CHIPS = [
  { key: 'blog.chip.antiAging', emoji: '✨' },
  { key: 'blog.chip.hydration', emoji: '💧' },
  { key: 'blog.chip.sensitiveSkin', emoji: '🌿' },
  { key: 'blog.chip.nightRoutine', emoji: '🌙' },
  { key: 'blog.chip.ingredients', emoji: '🔬' },
  { key: 'blog.chip.layering', emoji: '🧴' },
  { key: 'blog.chip.glassSkin', emoji: '💎' },
  { key: 'blog.chip.pdrnScience', emoji: '🧬' },
];

export default function BlogIndex() {
  const { t } = useLanguage();
  const articles = getAllArticles();

  return (
    <>
      <SEOHead
        title={`${t('blog.title')} | PDRN Skincare`}
        description={t('blog.metaDescription')}
        url={`${BASE_URL}/blog`}
        breadcrumbs={[
          { name: t('nav.home'), url: BASE_URL },
          { name: t('blog.title') },
        ]}
      />
      <Header />
      <main className="min-h-screen">
        {/* Hero section */}
        <section
          className="py-16 md:py-24 relative overflow-hidden"
          style={{ background: 'linear-gradient(160deg, hsl(var(--section-from)) 0%, hsl(var(--section-to)) 60%, hsl(350 20% 96%) 100%)' }}
        >
          {/* Decorative blob */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -top-24 -right-24 w-80 h-80 rounded-full opacity-20 blur-3xl"
            style={{ background: 'radial-gradient(circle, hsl(var(--primary)), transparent 70%)' }}
          />
          <div className="container max-w-4xl relative">
            {/* Badge */}
          <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest text-primary bg-primary/10 rounded-full px-3 py-1 mb-5">
              <BookOpen className="w-3 h-3" aria-hidden="true" />
              {t('blog.badge')}
            </span>

            <h1 className="text-3xl md:text-5xl font-serif font-bold text-foreground mb-3 leading-tight">
              {t('blog.title')}
            </h1>
            <p className="text-muted-foreground text-lg mb-8 max-w-2xl">
              {t('blog.subtitle')}
            </p>

            {/* Topic chips */}
            <div className="flex flex-wrap gap-2 mb-10">
              {TOPIC_CHIPS.map(chip => (
                <span
                  key={chip.key}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium bg-card border border-border text-foreground shadow-sm hover:border-primary/40 hover:bg-primary/5 transition-colors"
                >
                  <span aria-hidden="true">{chip.emoji}</span>
                  {t(chip.key)}
                </span>
              ))}
            </div>

            {/* Article grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {articles.map(article => (
                <ArticleCard key={article.slug} article={article} />
              ))}
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
